import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  increment,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  PipelineAnalyticsDocument,
  PipelineAnalyticsDocumentClient,
  DailyStats,
  SourceTotals,
  AnalyticsIncrementOperation,
  TrendCalculation,
  AnalyticsServiceResponse,
  AnalyticsInitializationData,
  PpcFarmSellerIdSource,
  PPC_FARM_COLLECTIONS,
  PPC_FARM_DOCUMENTS
} from '../types/ppcFarmTypes';

/**
 * PPC Farm Analytics Service
 * 
 * Provides high-performance analytics using incremental aggregation strategy.
 * Updates metrics in real-time via atomic Firestore increment operations.
 * Maintains rolling 30-day window for historical trend analysis.
 */
export class PpcFarmAnalyticsService {
  private static readonly ANALYTICS_DOC_PATH = `${PPC_FARM_COLLECTIONS.METADATA}/${PPC_FARM_DOCUMENTS.PIPELINE_ANALYTICS}`;
  private static readonly MAX_DAILY_STATS = 30; // Rolling 30-day window
  
  /**
   * Initialize analytics document with baseline data
   * Should be called once during system setup
   */
  static async initializeAnalytics(initData: AnalyticsInitializationData): Promise<AnalyticsServiceResponse> {
    try {
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      
      // Check if already initialized
      const existingDoc = await getDoc(analyticsRef);
      if (existingDoc.exists()) {
        return {
          success: false,
          error: 'Analytics document already exists. Use updateAnalytics() instead.'
        };
      }
      
      const initialAnalytics: Omit<PipelineAnalyticsDocumentClient, 'lastUpdated'> = {
        totals: {
          newSellersFound: 0,
          sellersProcessed: 0,
          competitorsFound: 0,
          totalSellersInMaster: 0,
          sellersWithNoData: 0, // All-time count of sellers with no_data_available status
          sourceCounts: {
            keepa_topseller_daily: initData.totalSellersFromKeepa || 0,
            raw_scrape_g_sheet: initData.rawScrapeTotalSellers || 0,
            competitor_extraction: initData.competitorsTotalSellers || 0,
            target_ledger_migration: 0,
            keepa_keyword_search: 0
          },
          noDataReasonBreakdown: {
            keepa_api_no_data_returned: 0
          }
        },
        sourceBreakdown: {
          newSellersFound: {
            keepa_topseller_daily: 0,
            raw_scrape_g_sheet: 0,
            competitor_extraction: 0,
            target_ledger_migration: 0
          },
          sellersProcessed: {
            keepa_topseller_daily: 0,
            raw_scrape_g_sheet: 0,
            competitor_extraction: 0,
            target_ledger_migration: 0
          },
          competitorsFound: {
            keepa_topseller_daily: 0,
            raw_scrape_g_sheet: 0,
            competitor_extraction: 0,
            target_ledger_migration: 0
          }
        },
        dailyStats: [],
        pipelineStatus: {
          ingestion: {
            lastRunTimestamp: new Date() as any,
            lastRunOutcome: 'success',
            durationSeconds: 0,
            fetchedFromKeepa: 0,
            newSellersAdded: 0,
            keepaTokensLeft: 0,
            errorMessage: null
          },
          enrichment: {
            lastProcessedTimestamp: new Date() as any,
            processedLastRun: 0,
            newCompetitorsAdded: 0
          },
          overrideStatus: {
            isOverridden: false,
            message: ''
          }
        },
        version: 1
      };
      
      await setDoc(analyticsRef, {
        ...initialAnalytics,
        lastUpdated: Timestamp.now()
      });
      
      console.log('✅ Analytics document initialized successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error initializing analytics:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Increment analytics counters atomically
   * High-performance operation using Firestore's increment()
   */
  static async incrementMetric(operation: AnalyticsIncrementOperation): Promise<AnalyticsServiceResponse> {
    try {
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      const today = operation.date || this.getTodayString();
      
      // Prepare atomic increment operations
      const updates: Record<string, any> = {
        [`totals.${operation.field}`]: increment(operation.amount),
        [`sourceBreakdown.${operation.field}.${operation.source}`]: increment(operation.amount),
        lastUpdated: Timestamp.now()
      };
      
      // Update the document atomically
      await updateDoc(analyticsRef, updates);
      
      // Update daily stats (separate operation to avoid conflicts)
      await this.updateDailyStats(operation.field, operation.amount, today);
      
      return { success: true };
      
    } catch (error) {
      console.error(`❌ Error incrementing ${operation.field}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Convenience method: Increment new sellers found
   */
  static async incrementNewSellersFound(
    source: PpcFarmSellerIdSource, 
    amount: number = 1
  ): Promise<AnalyticsServiceResponse> {
    return this.incrementMetric({
      field: 'newSellersFound',
      source,
      amount
    });
  }
  
  /**
   * Convenience method: Increment sellers processed
   */
  static async incrementSellersProcessed(
    source: PpcFarmSellerIdSource, 
    amount: number = 1
  ): Promise<AnalyticsServiceResponse> {
    return this.incrementMetric({
      field: 'sellersProcessed',
      source,
      amount
    });
  }
  
  /**
   * Convenience method: Increment competitors found
   */
  static async incrementCompetitorsFound(
    source: PpcFarmSellerIdSource, 
    amount: number = 1
  ): Promise<AnalyticsServiceResponse> {
    return this.incrementMetric({
      field: 'competitorsFound',
      source,
      amount
    });
  }
  
  /**
   * Get complete analytics document
   */
  static async getAnalytics(): Promise<PipelineAnalyticsDocumentClient | null> {
    try {
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      const docSnap = await getDoc(analyticsRef);
      
      if (!docSnap.exists()) {
        console.warn('⚠️ Analytics document does not exist. Call initializeAnalytics() first.');
        return null;
      }
      
      return docSnap.data() as PipelineAnalyticsDocumentClient;
      
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      return null;
    }
  }
  
  /**
   * Calculate trends from daily statistics
   * Handles both array format and map format from Firestore
   */
  static calculateTrends(
    dailyStats: DailyStats[] | Record<string, any> | undefined | null,
    field: keyof DailyStats
  ): TrendCalculation {
    // Handle null/undefined dailyStats
    if (!dailyStats) {
      console.warn('⚠️ dailyStats is missing');
      return { last24h: 0, last7d: 0, last30d: 0 };
    }

    const now = new Date();
    const today = this.getTodayString();
    
    // Calculate date thresholds
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = this.formatDateString(yesterday);
    
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // Calculate trends
    let last24h = 0;
    let last7d = 0;
    let last30d = 0;
    
    // Handle both array and map formats
    if (Array.isArray(dailyStats)) {
      // Array format (expected structure)
      dailyStats.forEach(stat => {
        const statDate = new Date(stat.date);
        const value = typeof stat[field] === 'number' ? stat[field] as number : 0;
        
        // Last 24 hours (today + yesterday)
        if (stat.date === today || stat.date === yesterdayStr) {
          last24h += value;
        }
        
        // Last 7 days
        if (statDate >= sevenDaysAgo) {
          last7d += value;
        }
        
        // Last 30 days
        if (statDate >= thirtyDaysAgo) {
          last30d += value;
        }
      });
    } else if (typeof dailyStats === 'object') {
      // Map format (current Firestore structure)
      console.log('🔄 Processing dailyStats as map format');
      
      Object.entries(dailyStats).forEach(([dateStr, statData]: [string, any]) => {
        // Skip non-date fields like lastUpdatedAt
        if (!dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return;
        }
        
        const statDate = new Date(dateStr);
        const value = typeof statData[field] === 'number' ? statData[field] as number : 0;
        
        // Last 24 hours (today + yesterday)
        if (dateStr === today || dateStr === yesterdayStr) {
          last24h += value;
        }
        
        // Last 7 days
        if (statDate >= sevenDaysAgo) {
          last7d += value;
        }
        
        // Last 30 days
        if (statDate >= thirtyDaysAgo) {
          last30d += value;
        }
      });
    } else {
      console.warn('⚠️ dailyStats is not in expected format:', typeof dailyStats);
    }
    
    console.log(`📊 Calculated trends for ${field}:`, { last24h, last7d, last30d });
    return { last24h, last7d, last30d };
  }
  
  /**
   * Calculate processing velocity (items per day)
   * Handles both array format and map format from Firestore
   */
  static calculateVelocity(dailyStats: DailyStats[] | Record<string, any> | undefined | null): {
    avgSellersProcessedPerDay: number;
    avgCompetitorsFoundPerDay: number;
  } {
    // Handle null/undefined dailyStats
    if (!dailyStats) {
      console.warn('⚠️ dailyStats is missing for velocity calculation');
      return { avgSellersProcessedPerDay: 0, avgCompetitorsFoundPerDay: 0 };
    }

    let totalSellersProcessed = 0;
    let totalCompetitorsFound = 0;
    let dayCount = 0;
    
    // Handle both array and map formats
    if (Array.isArray(dailyStats)) {
      // Array format (expected structure)
      if (dailyStats.length === 0) {
        return { avgSellersProcessedPerDay: 0, avgCompetitorsFoundPerDay: 0 };
      }
      
      totalSellersProcessed = (dailyStats as any[]).reduce((sum: number, stat: any) => sum + (stat.sellersProcessed || 0), 0);
      totalCompetitorsFound = (dailyStats as any[]).reduce((sum: number, stat: any) => sum + (stat.competitorsFound || 0), 0);
      dayCount = dailyStats.length;
    } else if (typeof dailyStats === 'object') {
      // Map format (current Firestore structure)
      console.log('🔄 Processing dailyStats as map format for velocity');
      
      Object.entries(dailyStats).forEach(([dateStr, statData]: [string, any]) => {
        // Skip non-date fields like lastUpdatedAt
        if (!dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return;
        }
        
        totalSellersProcessed += statData.sellersProcessed || 0;
        totalCompetitorsFound += statData.competitorsFound || 0;
        dayCount++;
      });
    }
    
    if (dayCount === 0) {
      return { avgSellersProcessedPerDay: 0, avgCompetitorsFoundPerDay: 0 };
    }
    
    const avgSellersProcessedPerDay = Math.round(totalSellersProcessed / dayCount);
    const avgCompetitorsFoundPerDay = Math.round(totalCompetitorsFound / dayCount);
    
    console.log(`📊 Calculated velocity:`, { avgSellersProcessedPerDay, avgCompetitorsFoundPerDay, dayCount });
    return { avgSellersProcessedPerDay, avgCompetitorsFoundPerDay };
  }
  
  /**
   * Update source totals (for periodic recalculation)
   */
  static async updateSourceTotals(): Promise<AnalyticsServiceResponse> {
    try {
      // Count sellers by source from ppcFarm_sellerIds collection
      const sellerIdsRef = collection(db, PPC_FARM_COLLECTIONS.SELLER_IDS);
      
      const [keepaCount, rawScrapeCount, competitorCount, targetLedgerCount] = await Promise.all([
        getCountFromServer(query(sellerIdsRef, where('source', '==', 'keepa_topseller_daily'))),
        getCountFromServer(query(sellerIdsRef, where('source', '==', 'raw_scrape_g_sheet'))),
        getCountFromServer(query(sellerIdsRef, where('source', '==', 'competitor_extraction'))),
        getCountFromServer(query(sellerIdsRef, where('source', '==', 'target_ledger_migration')))
      ]);
      
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      await updateDoc(analyticsRef, {
        'totals.sourceCounts.keepa_topseller_daily': keepaCount.data().count,
        'totals.sourceCounts.raw_scrape_g_sheet': rawScrapeCount.data().count,
        'totals.sourceCounts.competitor_extraction': competitorCount.data().count,
        'totals.sourceCounts.target_ledger_migration': targetLedgerCount.data().count,
        lastUpdated: Timestamp.now()
      });
      
      console.log('✅ Source totals updated successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error updating source totals:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Cleanup old daily statistics (keep only last 30 days)
   */
  static async cleanupOldStats(): Promise<AnalyticsServiceResponse> {
    try {
      const analytics = await this.getAnalytics();
      if (!analytics) {
        return { success: false, error: 'Analytics document not found' };
      }
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const cutoffDate = this.formatDateString(thirtyDaysAgo);
      
      // Filter out stats older than 30 days
      const filteredStats = analytics.dailyStats.filter(stat => stat.date >= cutoffDate);
      
      // Only update if we actually removed some stats
      if (filteredStats.length < analytics.dailyStats.length) {
        const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
        await updateDoc(analyticsRef, {
          dailyStats: filteredStats,
          lastUpdated: Timestamp.now()
        });
        
        console.log(`✅ Cleaned up ${analytics.dailyStats.length - filteredStats.length} old daily stats`);
      }
      
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error cleaning up old stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Update daily statistics for a specific date
   * Private method used by incrementMetric
   */
  private static async updateDailyStats(
    field: 'newSellersFound' | 'sellersProcessed' | 'competitorsFound',
    amount: number,
    date: string
  ): Promise<void> {
    try {
      const analytics = await this.getAnalytics();
      if (!analytics) {
        console.warn('⚠️ Cannot update daily stats: Analytics document not found');
        return;
      }
      
      // Find existing stat for the date or create new one
      const existingStatIndex = analytics.dailyStats.findIndex(stat => stat.date === date);
      let updatedStats: DailyStats[];
      
      if (existingStatIndex >= 0) {
        // Update existing stat
        updatedStats = [...analytics.dailyStats];
        updatedStats[existingStatIndex] = {
          ...updatedStats[existingStatIndex],
          [field]: updatedStats[existingStatIndex][field] + amount
        };
      } else {
        // Create new stat for the date
        const newStat: DailyStats = {
          date,
          newSellersFound: field === 'newSellersFound' ? amount : 0,
          sellersProcessed: field === 'sellersProcessed' ? amount : 0,
          competitorsFound: field === 'competitorsFound' ? amount : 0
        };
        
        updatedStats = [...analytics.dailyStats, newStat];
        
        // Sort by date (newest first) and limit to 30 days
        updatedStats.sort((a, b) => b.date.localeCompare(a.date));
        updatedStats = updatedStats.slice(0, this.MAX_DAILY_STATS);
      }
      
      // Update the document
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      await updateDoc(analyticsRef, {
        dailyStats: updatedStats,
        lastUpdated: Timestamp.now()
      });
      
    } catch (error) {
      console.error('❌ Error updating daily stats:', error);
      // Don't throw - daily stats update failures shouldn't break main operations
    }
  }
  
  /**
   * Get today's date string in YYYY-MM-DD format
   */
  private static getTodayString(): string {
    return this.formatDateString(new Date());
  }
  
  /**
   * Format date as YYYY-MM-DD string
   */
  private static formatDateString(date: Date): string {
    return date.toISOString().split('T')[0];
  }
  
  /**
   * Non-blocking analytics update wrapper
   * Use this in production functions to ensure analytics failures don't break main operations
   */
  static async safeIncrementMetric(operation: AnalyticsIncrementOperation): Promise<void> {
    try {
      await this.incrementMetric(operation);
    } catch (error) {
      console.warn('⚠️ Analytics update failed (non-blocking):', error);
      // Don't throw - analytics failures shouldn't break main pipeline
    }
  }
  
  /**
   * Batch increment multiple metrics atomically
   * Useful for operations that affect multiple counters
   */
  static async batchIncrementMetrics(operations: AnalyticsIncrementOperation[]): Promise<AnalyticsServiceResponse> {
    try {
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      const today = this.getTodayString();
      
      // Prepare all atomic increment operations
      const updates: Record<string, any> = {
        lastUpdated: Timestamp.now()
      };
      
      operations.forEach(operation => {
        const opDate = operation.date || today;
        updates[`totals.${operation.field}`] = increment(operation.amount);
        updates[`sourceBreakdown.${operation.field}.${operation.source}`] = increment(operation.amount);
      });
      
      // Update the document atomically
      await updateDoc(analyticsRef, updates);
      
      // Update daily stats for each operation
      for (const operation of operations) {
        const opDate = operation.date || today;
        await this.updateDailyStats(operation.field, operation.amount, opDate);
      }
      
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error in batch increment:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Update pipeline status fields in the analytics document
   * This replaces the need for the separate keepaPipelineStatus document
   */
  static async updatePipelineStatus(updates: {
    ingestion?: Partial<{
      lastRunTimestamp: any;
      lastRunOutcome: 'success' | 'failure';
      durationSeconds: number;
      fetchedFromKeepa: number;
      newSellersAdded: number;
      keepaTokensLeft: number;
      errorMessage: string | null;
    }>;
    enrichment?: Partial<{
      lastProcessedTimestamp: any;
      processedLastRun: number;
      newCompetitorsAdded: number;
    }>;
    overrideStatus?: Partial<{
      isOverridden: boolean;
      message: string;
    }>;
  }): Promise<AnalyticsServiceResponse> {
    try {
      const analyticsRef = doc(db, this.ANALYTICS_DOC_PATH);
      
      // Prepare update object with nested field paths
      const updateData: Record<string, any> = {
        lastUpdated: Timestamp.now()
      };
      
      // Add ingestion updates
      if (updates.ingestion) {
        Object.entries(updates.ingestion).forEach(([key, value]) => {
          updateData[`pipelineStatus.ingestion.${key}`] = value;
        });
      }
      
      // Add enrichment updates
      if (updates.enrichment) {
        Object.entries(updates.enrichment).forEach(([key, value]) => {
          updateData[`pipelineStatus.enrichment.${key}`] = value;
        });
      }
      
      // Add override status updates
      if (updates.overrideStatus) {
        Object.entries(updates.overrideStatus).forEach(([key, value]) => {
          updateData[`pipelineStatus.overrideStatus.${key}`] = value;
        });
      }
      
      await updateDoc(analyticsRef, updateData);
      
      console.log('✅ Pipeline status updated successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error updating pipeline status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Convenience method: Update ingestion status (used by getTopSellerIds function)
   */
  static async updateIngestionStatus(status: {
    lastRunTimestamp: any;
    lastRunOutcome: 'success' | 'failure';
    durationSeconds: number;
    fetchedFromKeepa: number;
    newSellersAdded: number;
    keepaTokensLeft: number;
    errorMessage: string | null;
  }): Promise<AnalyticsServiceResponse> {
    return this.updatePipelineStatus({ ingestion: status });
  }
  
  /**
   * Convenience method: Update enrichment status (used by getSellerDetails function)
   */
  static async updateEnrichmentStatus(status: {
    lastProcessedTimestamp: any;
    processedLastRun: number;
    newCompetitorsAdded?: number;
  }): Promise<AnalyticsServiceResponse> {
    return this.updatePipelineStatus({ enrichment: status });
  }
}